import { Trans, useTranslation } from "react-i18next";
import SlideUpDialog from "../../SlideUpDialog";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import IconLoader from "../../IconLoader";
import ButtonWithAnalytics from "../../ButtonWithAnalytics";
import {
  convertPxToRem,
  formatDateToReadableString,
  mapServiceOfferingUsageItemsForTranslationKeys,
} from "@/utils";
import { useState } from "react";
import {
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
} from "@/services/core-api-adapter";

interface AvailablePromoCodesModalV2Props {
  isModalOpen: boolean;
  onModalClose: () => void;
  onContinueButtonClick: (selectedVoucher?: string) => void;
  serviceOfferingUsageItems: MemberServiceOfferingUsage[];
}

export default function AvailablePromoCodesModalV2({
  isModalOpen,
  onModalClose,
  onContinueButtonClick,
  serviceOfferingUsageItems,
}: AvailablePromoCodesModalV2Props) {
  const { t } = useTranslation();
  const [selectedVoucherOrPromo, setSelectedVoucherOrPromo] = useState<any>(
    serviceOfferingUsageItems[0]
  );

  function handleVoucherOrPromoValueChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const selectedId = event.target.value;
    const selectedItem = serviceOfferingUsageItems.find(
      (item) => item.id === selectedId
    );
    setSelectedVoucherOrPromo(selectedItem || "CardPayment");
  }

  function handleContinueButtonClick() {
    if (selectedVoucherOrPromo === "CardPayment") {
      onContinueButtonClick(undefined);
      return;
    }
    if (selectedVoucherOrPromo) {
      onContinueButtonClick(selectedVoucherOrPromo);
    } else {
      onContinueButtonClick();
    }
  }

  return (
    <SlideUpDialog
      isOpen={isModalOpen}
      onClose={() => onModalClose()}
      label={t("AvailableVoucherCodesModal.title")}
    >
      <Stack textAlign="center" spacing={3}>
        <IconLoader
          icon="TagWithCheckIcon"
          color="primary"
          sx={{ fontSize: convertPxToRem(90), alignSelf: "center" }}
        />

        <Stack spacing={1}>
          <Typography variant="h3" fontWeight={600}>
            {t("AvailableVoucherCodesModal.title")}
          </Typography>

          <Typography variant="h4" fontWeight={400}>
            {t("AvailableVoucherCodesModal.subtitle")}
          </Typography>
        </Stack>

        <Stack borderRadius={2} my={2} p={2}>
          <Stack
            component="ul"
            role="list"
            aria-label={t("AvailableVoucherCodesModal.VoucherCodeLabel")}
          >
            <FormControl>
              <RadioGroup
                aria-labelledby={t(`ChooseVoucherOrPromo.VoucherOrPromoSelect`)}
                value={selectedVoucherOrPromo?.id || "CardPayment"}
                onChange={handleVoucherOrPromoValueChange}
              >
                {serviceOfferingUsageItems.map(
                  (serviceOfferingUsageItem, index) => (
                    <>
                      <FormControlLabel
                        key={index}
                        value={serviceOfferingUsageItem.id}
                        control={<Radio />}
                        label={
                          <Typography fontWeight="500" color="neutral.700">
                            {t(
                              `AvailableVoucherCodesModal.${mapServiceOfferingUsageItemsForTranslationKeys(serviceOfferingUsageItem)}.label`
                            )}
                            {serviceOfferingUsageItem.serviceOfferingType ===
                              MemberServiceOfferingType.PATHOLOGY && (
                              <>
                                :{" "}
                                {t(
                                  `PathologyOrderSummary.${serviceOfferingUsageItem.chargeCode}.title` as any
                                )}
                              </>
                            )}
                          </Typography>
                        }
                      />
                      {!serviceOfferingUsageItem.voucherDetails ? (
                        <Typography
                          variant="body2"
                          textAlign="left"
                          marginLeft={4}
                          fontWeight="500"
                          color="neutral.700"
                        >
                          {t("AvailableVoucherCodesModal.PromoFooterText", {
                            validUntilDate: formatDateToReadableString(
                              new Date(serviceOfferingUsageItem.availableTo)
                            ),
                          })}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          textAlign="left"
                          marginLeft={4}
                          fontWeight="400"
                          color="neutral.700"
                        >
                          {t("AvailableVoucherCodesModal.VoucherFooterText", {
                            validUntilDate: formatDateToReadableString(
                              new Date(serviceOfferingUsageItem.availableTo)
                            ),
                          })}
                        </Typography>
                      )}
                    </>
                  )
                )}
                <FormControlLabel
                  value={"CardPayment"}
                  control={<Radio />}
                  sx={{ marginTop: 3 }}
                  label={
                    <Typography fontWeight="500" color="neutral.700">
                      {t("AvailableVoucherCodesModal.CardPayment.label")}
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2}>
          <ButtonWithAnalytics
            intent="confirmational"
            page="AvailablePromoCodes"
            text={t("AvailableVoucherCodesModal.VoucherCode.buttonText")}
            fullWidth
            variant="contained"
            disabled={!selectedVoucherOrPromo}
            onClick={handleContinueButtonClick}
          >
            {t("AvailableVoucherCodesModal.VoucherCode.buttonText")}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    </SlideUpDialog>
  );
}
